import { Component, inject, OnInit, signal, ChangeDetectionStrategy, effect, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { switchMap, catchError, of, from } from 'rxjs';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, AbstractControl, ValidationErrors, AsyncValidatorFn } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { SiteService } from '../../../core/services/site.service';
import { AuthService } from '../../../core/auth/auth.service';
import { OrganizationManageService } from '../../../core/services/organization-manage.service';
import { SiteManagementDepartmentService } from '../../../core/services/site-management-department.service';
import { SiteManagementDepartment } from '../../../shared/interfaces';
import { GeoPoint, doc, getDoc } from 'firebase/firestore';
import { FIRESTORE_TOKEN } from '../../../core/firebase/firestore.provider';

const DEFAULT_GEOPOINT = { latitude: 34.6937, longitude: 135.5023 };

@Component({
  selector: 'app-site-form',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    RouterModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatSnackBarModule
  ],
  template: `
    <div class="p-6 max-w-3xl mx-auto">
      <div class="flex items-center mb-6">
        <button matIconButton aria-label="戻る" (click)="goBack()" class="mr-4">
          <mat-icon>arrow_back</mat-icon>
        </button>
        <h1 class="text-2xl font-bold">{{ isEditMode() ? '現場の編集' : '新規現場の登録' }}</h1>
      </div>

      <div class="bg-primary-container rounded-lg shadow-sm p-6">
        @if (isLoading()) {
          <div class="flex justify-center items-center py-12">
            <mat-spinner diameter="40"></mat-spinner>
          </div>
        } @else {
          <form [formGroup]="form" (ngSubmit)="onSubmit()" class="flex flex-col gap-4">
            
            @if (!isEditMode()) {
              <mat-form-field appearance="outline" class="w-full">
                <mat-label>現場ID (半角英数字・ハイフン 3〜30文字)</mat-label>
                <input matInput formControlName="siteId" placeholder="例: Tokyo-Building-01" required>
                @if (form.get('siteId')?.hasError('required') && form.get('siteId')?.touched) {
                  <mat-error>現場IDは必須です</mat-error>
                }
                @if (form.get('siteId')?.hasError('pattern') && form.get('siteId')?.touched) {
                  <mat-error>半角英数字、ハイフンのみ使用できます</mat-error>
                }
                @if ((form.get('siteId')?.hasError('minlength') || form.get('siteId')?.hasError('maxlength')) && form.get('siteId')?.touched) {
                  <mat-error>3文字以上30文字以下で入力してください</mat-error>
                }
                @if (form.get('siteId')?.hasError('uniqueSiteId') && form.get('siteId')?.touched) {
                  <mat-error>この現場IDは既に使用されています</mat-error>
                }
                @if (form.get('siteId')?.pending) {
                  <mat-hint>確認中...</mat-hint>
                }
              </mat-form-field>

              <mat-form-field appearance="outline" class="w-full">
                <mat-label>パスワード (6文字以上)</mat-label>
                <input matInput [type]="hidePassword() ? 'password' : 'text'" formControlName="password" placeholder="現場・閲覧用パスワード" required>
                <button matIconButton matSuffix type="button" (click)="hidePassword.set(!hidePassword())" [attr.aria-label]="hidePassword() ? 'パスワードを表示' : 'パスワードを非表示'">
                  <mat-icon>{{ hidePassword() ? 'visibility_off' : 'visibility' }}</mat-icon>
                </button>
                @if (form.get('password')?.hasError('required') && form.get('password')?.touched) {
                  <mat-error>パスワードは必須です</mat-error>
                }
                @if (form.get('password')?.hasError('minlength') && form.get('password')?.touched) {
                  <mat-error>パスワードは6文字以上で入力してください</mat-error>
                }
              </mat-form-field>
            }

            <mat-form-field appearance="outline" class="w-full">
              <mat-label>現場名</mat-label>
              <input matInput formControlName="name" placeholder="例: 東京ビルディング" required>
              @if (form.get('name')?.hasError('required') && form.get('name')?.touched) {
                <mat-error>現場名は必須です</mat-error>
              }
            </mat-form-field>

            <mat-form-field appearance="outline" class="w-full">
              <mat-label>住所1（都道府県・市区町村・番地）</mat-label>
              <input matInput formControlName="address1" placeholder="例: 東京都千代田区丸の内1-1-1" required>
              @if (form.get('address1')?.hasError('required') && form.get('address1')?.touched) {
                <mat-error>住所1は必須です</mat-error>
              }
            </mat-form-field>

            <mat-form-field appearance="outline" class="w-full">
              <mat-label>住所2（建物名・階数など）</mat-label>
              <input matInput formControlName="address2" placeholder="例: 10階">
            </mat-form-field>

            @if (authService.userRole() === 'super_admin' && !isEditMode()) {
              <mat-form-field appearance="outline" class="w-full">
                <mat-label>所属組織</mat-label>
                <mat-select formControlName="organizationId" required>
                  @for (org of organizationManageService.organizations(); track org.id) {
                    <mat-option [value]="org.id">{{ org.name }}</mat-option>
                  }
                </mat-select>
                @if (form.get('organizationId')?.hasError('required') && form.get('organizationId')?.touched) {
                  <mat-error>所属組織は必須です</mat-error>
                }
              </mat-form-field>
            }

            <mat-form-field appearance="outline" class="w-full">
              <mat-label>管理部署</mat-label>
              <mat-select formControlName="managementDepartmentId" required>
                @for (dept of availableDepartments(); track dept.id) {
                  <mat-option [value]="dept.id">{{ dept.name }}</mat-option>
                }
              </mat-select>
              @if (form.get('managementDepartmentId')?.hasError('required') && form.get('managementDepartmentId')?.touched) {
                <mat-error>管理部署は必須です</mat-error>
              }
            </mat-form-field>

            <div class="flex justify-end gap-4 mt-6">
              <button matButton type="button" (click)="goBack()">キャンセル</button>
              <button matButton="tonal" color="primary" type="submit" [disabled]="form.invalid || isSaving()">
                @if (isSaving()) {
                  <mat-spinner diameter="20" class="mr-2 inline-block"></mat-spinner>
                  保存中...
                } @else {
                  保存
                }
              </button>
            </div>
          </form>
        }
      </div>
    </div>
  `
})
export class SiteFormComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly siteService = inject(SiteService);
  public readonly authService = inject(AuthService);
  public readonly organizationManageService = inject(OrganizationManageService);
  private readonly siteManagementDepartmentService = inject(SiteManagementDepartmentService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly snackBar = inject(MatSnackBar);
  private readonly firestore = inject(FIRESTORE_TOKEN);

  public readonly isEditMode = signal(false);
  public readonly isLoading = signal(false);
  public readonly isSaving = signal(false);

  public readonly availableDepartments = signal<SiteManagementDepartment[]>([]);
  public readonly hidePassword = signal(true);
  private siteId: string | null = null;

  public form: FormGroup = this.fb.group({
    siteId: [''],
    password: [''],
    name: ['', Validators.required],
    address1: ['', Validators.required],
    address2: [''],
    managementDepartmentId: ['', Validators.required],
    organizationId: ['']
  });

  private dataLoaded = false;

  constructor() {
    effect(() => {
      if (this.authService.userRole() !== 'super_admin') {
        this.availableDepartments.set(this.siteManagementDepartmentService.departments());
      }
    });

    effect(() => {
      if (this.isEditMode() && this.siteId && !this.dataLoaded) {
        if (this.siteService.isSitesResolved()) {
          const error = this.siteService.error();
          if (error) {
            this.isLoading.set(false);
            this.snackBar.open('現場データの取得に失敗しました', '閉じる', { duration: 3000 });
            this.router.navigate(['/sites/manage']);
            return;
          }

          const sites = this.siteService.sites();
          const site = sites.find(s => s.id === this.siteId);
          if (site) {
            const patchData = () => {
              this.form.patchValue({
                name: site.name,
                address1: site.address1,
                address2: site.address2,
                managementDepartmentId: site.managementDepartmentId,
                organizationId: site.organizationId
              }, { emitEvent: false });
              this.dataLoaded = true;
              this.isLoading.set(false);
            };

            if (this.authService.userRole() === 'super_admin' && site.organizationId) {
              this.siteManagementDepartmentService.getDepartmentsByOrganizationId(site.organizationId)
                .then(depts => {
                  this.availableDepartments.set(depts);
                  patchData();
                })
                .catch(() => {
                  this.availableDepartments.set([]);
                  patchData();
                });
            } else {
              patchData();
            }
          } else {
            this.isLoading.set(false);
            this.snackBar.open('現場データが見つかりませんでした', '閉じる', { duration: 3000 });
            this.router.navigate(['/sites/manage']);
          }
        }
      }
    });
  }

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.isEditMode.set(true);
      this.siteId = id;
      this.isLoading.set(true);
      // Disable siteId and password in edit mode (they are not submitted or shown)
      this.form.get('siteId')?.disable();
      this.form.get('password')?.disable();
    } else {
      // Setup validators for create mode
      this.form.get('siteId')?.setValidators([
        Validators.required,
        Validators.minLength(3),
        Validators.maxLength(30),
        Validators.pattern(/^[a-zA-Z0-9-]+$/)
      ]);
      this.form.get('siteId')?.setAsyncValidators([this.uniqueSiteIdValidator()]);
      this.form.get('password')?.setValidators([
        Validators.required,
        Validators.minLength(6)
      ]);
      this.form.get('siteId')?.updateValueAndValidity();
      this.form.get('password')?.updateValueAndValidity();

      if (this.authService.userRole() === 'super_admin') {
        this.form.get('organizationId')?.setValidators(Validators.required);
        this.form.get('organizationId')?.updateValueAndValidity();
      }
    }

    if (this.authService.userRole() === 'super_admin') {
      this.form.get('organizationId')?.valueChanges.pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap(orgId => {
          this.form.get('managementDepartmentId')?.setValue('');
          if (!orgId) {
            this.availableDepartments.set([]);
            return of(null);
          }
          return from(this.siteManagementDepartmentService.getDepartmentsByOrganizationId(orgId)).pipe(
            catchError(() => {
              this.availableDepartments.set([]);
              this.snackBar.open('管理部署の取得に失敗しました', '閉じる', { duration: 3000 });
              return of(null);
            })
          );
        })
      ).subscribe((depts) => {
        if (depts) {
          this.availableDepartments.set(depts);
        }
      });
    }
  }

  public goBack(): void {
    this.router.navigate(['/sites/manage']);
  }

  private uniqueSiteIdValidator(): AsyncValidatorFn {
    return async (control: AbstractControl): Promise<ValidationErrors | null> => {
      if (!control.value) return null;
      try {
        const docRef = doc(this.firestore, 'sites', control.value);
        const docSnap = await getDoc(docRef);
        return docSnap.exists() ? { uniqueSiteId: true } : null;
      } catch (error) {
        console.error('Error checking unique siteId', error);
        return null;
      }
    };
  }

  private async geocodeAddress(): Promise<{ latitude: number; longitude: number }> {
    const address1 = this.form.get('address1')?.value;
    const address2 = this.form.get('address2')?.value || '';
    const fullAddress = `${address1} ${address2}`.trim();

    try {
      const geocoder = new google.maps.Geocoder();
      const response = await geocoder.geocode({ address: fullAddress });

      if (response.results && response.results.length > 0) {
        const location = response.results[0].geometry.location;
        return { latitude: location.lat(), longitude: location.lng() };
      }
    } catch (error) {
      console.error('Geocoding error', error);
    }
    // Geocoding に失敗した場合はデフォルト座標を返す
    return DEFAULT_GEOPOINT;
  }

  public async onSubmit(): Promise<void> {
    if (this.form.invalid) return;

    this.isSaving.set(true);
    const formValue = this.form.value;

    // 住所から座標を自動取得
    const coords = await this.geocodeAddress();
    
    const data = {
      name: formValue.name,
      address1: formValue.address1,
      address2: formValue.address2 || '',
      geopoint: new GeoPoint(coords.latitude, coords.longitude),
      managementDepartmentId: formValue.managementDepartmentId
    };

    try {
      if (this.isEditMode() && this.siteId) {
        await this.siteService.updateSite(this.siteId, data);
        this.snackBar.open('現場を更新しました', '閉じる', { duration: 3000 });
      } else {
        const orgId = this.authService.userRole() === 'super_admin' ? formValue.organizationId : undefined;
        const createData = {
          ...data,
          siteId: formValue.siteId,
          password: formValue.password
        };
        await this.siteService.createSite(createData, orgId);
        this.snackBar.open('新規現場を作成しました', '閉じる', { duration: 3000 });
      }
      this.siteService.reloadSites();
      this.router.navigate(['/sites/manage']);
    } catch (error) {
      console.error('Failed to save site', error);
      this.snackBar.open('現場の保存に失敗しました', '閉じる', { duration: 3000 });
    } finally {
      this.isSaving.set(false);
    }
  }
}
